import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  ChevronLeft, ChevronRight, Plus, Trash2, CalendarDays, Clock, Repeat, Wallet,
} from "lucide-react";
import {
  format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  eachDayOfInterval, isSameDay, isSameMonth, addMonths, addWeeks, parseISO,
} from "date-fns";
import { it } from "date-fns/locale";

const STATUS = {
  confermato: { label: "Confermato", color: "#10B981" },
  in_attesa: { label: "In attesa", color: "#F59E0B" },
  completato: { label: "Completato", color: "#3B82F6" },
  annullato: { label: "Annullato", color: "#EF4444" },
};

const pad = (n) => String(n).padStart(2, "0");
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeStr = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const isoFrom = (dateStr, timeStr) => new Date(`${dateStr}T${timeStr || "00:00"}`).toISOString();
const addMinToTime = (timeStr, min) => {
  const [h, m] = (timeStr || "09:00").split(":").map(Number);
  return toTimeStr(new Date(2000, 0, 1, h, m + (min || 0)));
};
function makeSlots(dataInizio, numero, cadenza, oraI, oraF, existing = []) {
  const base = new Date(`${dataInizio}T00:00`);
  const out = [];
  for (let i = 0; i < numero; i++) {
    const d = new Date(base.getTime());
    d.setDate(d.getDate() + cadenza * i);
    const ex = existing[i];
    out.push({ data: ex?.data || toDateStr(d), ora_inizio: ex?.ora_inizio || oraI, ora_fine: ex?.ora_fine || oraF });
  }
  return out;
}

export default function Calendario() {
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [appts, setAppts] = useState([]);
  const [clients, setClients] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [services, setServices] = useState([]);
  const [methods, setMethods] = useState([]);
  const [filterCol, setFilterCol] = useState("all");
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClient, setNewClient] = useState({ nome: "", cognome: "", telefono: "", email: "" });
  const [creatingClient, setCreatingClient] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const range = useMemo(() => {
    if (view === "day") return { start: new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()), end: addDays(cursor, 1) };
    if (view === "week") return { start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) };
    return { start: startOfMonth(cursor), end: endOfMonth(cursor) };
  }, [view, cursor]);

  const load = useCallback(async () => {
    try {
      const params = { start: range.start.toISOString(), end: addDays(range.end, 1).toISOString() };
      if (filterCol !== "all") params.collaborator_id = filterCol;
      const { data } = await api.get("/appointments", { params });
      setAppts(data);
    } catch { toast.error("Errore caricamento appuntamenti"); }
  }, [range, filterCol]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/collaborators").then(({ data }) => setCollaborators(data));
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/payment-methods").then(({ data }) => setMethods(data));
  }, []);

  const move = (dir) => {
    if (view === "day") setCursor((c) => addDays(c, dir));
    else if (view === "week") setCursor((c) => addWeeks(c, dir));
    else setCursor((c) => addMonths(c, dir));
  };

  const openNew = (day) => {
    const start = day ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0)
                      : (() => { const d = new Date(); d.setHours(9, 0, 0, 0); return d; })();
    const dataInizio = toDateStr(start);
    const dataFine = toDateStr(addDays(start, 56));
    const oraI = "09:00", oraF = "10:00";
    setEditing(null);
    setForm({
      client_id: "", collaborator_id: "", service_id: "", stato: "confermato", stanza: "", note: "", importo: 0,
      pagato: false, metodo_pagamento: "",
      dividi_importo: true, numero: 1, frequenza_settimanale: 2, data_inizio: dataInizio, data_fine: dataFine,
      ora_inizio: oraI, ora_fine: oraF,
      slots: makeSlots(dataInizio, 1, 4, oraI, oraF),
    });
    setOpen(true);
  };

  const openEdit = (a) => {
    const s = new Date(a.inizio), e = new Date(a.fine);
    setEditing(a);
    setForm({
      client_id: a.client_id, collaborator_id: a.collaborator_id || "", service_id: a.service_id || "",
      stato: a.stato, stanza: a.stanza || "", note: a.note || "", importo: a.importo || 0,
      pagato: a.pagato || false, metodo_pagamento: a.metodo_pagamento || "",
      dividi_importo: true, numero: 1, frequenza_settimanale: 2,
      data_inizio: toDateStr(s), data_fine: toDateStr(s), ora_inizio: toTimeStr(s), ora_fine: toTimeStr(e),
      slots: [{ data: toDateStr(s), ora_inizio: toTimeStr(s), ora_fine: toTimeStr(e) }],
    });
    setOpen(true);
  };

  const onServiceChange = (sid) => {
    const svc = services.find((s) => s.id === sid);
    setForm((f) => {
      const dur = svc?.durata_min || 60;
      const oraF = addMinToTime(f.ora_inizio, dur);
      let numero = f.numero;
      const m = svc?.nome?.match(/(\d+)/);
      if (m && !editing) numero = Math.min(parseInt(m[1]), 60);
      const step = Math.max(1, Math.round(7 / Math.max(1, f.frequenza_settimanale)));
      const slots = editing
        ? [{ ...f.slots[0], ora_fine: addMinToTime(f.slots[0].ora_inizio, dur) }]
        : makeSlots(f.data_inizio, numero, step, f.ora_inizio, oraF);
      return { ...f, service_id: sid, importo: svc ? svc.prezzo : f.importo, ora_fine: oraF, numero, slots };
    });
  };

  const stepFreq = (freq) => Math.max(1, Math.round(7 / Math.max(1, freq)));
  const setNumero = (val) => {
    if (val === "") { setForm((f) => ({ ...f, numero: "" })); return; }
    const n = Math.max(1, Math.min(parseInt(val) || 1, 60));
    setForm((f) => ({ ...f, numero: n, slots: makeSlots(f.data_inizio, n, stepFreq(f.frequenza_settimanale), f.ora_inizio, f.ora_fine, f.slots) }));
  };
  const normalizeNumero = () => setForm((f) => {
    const n = Math.max(1, Math.min(parseInt(f.numero) || 1, 60));
    return { ...f, numero: n, slots: makeSlots(f.data_inizio, n, stepFreq(f.frequenza_settimanale), f.ora_inizio, f.ora_fine, f.slots) };
  });
  const setFrequenza = (v) => setForm((f) => ({ ...f, frequenza_settimanale: v, slots: makeSlots(f.data_inizio, f.numero, stepFreq(v), f.ora_inizio, f.ora_fine, f.slots) }));
  const setDataInizio = (v) => setForm((f) => ({ ...f, data_inizio: v, slots: makeSlots(v, f.numero, stepFreq(f.frequenza_settimanale), f.ora_inizio, f.ora_fine, f.slots) }));
  const setDataFine = (v) => setForm((f) => ({ ...f, data_fine: v }));
  const updateSlot = (i, key, val) => setForm((f) => { const slots = [...f.slots]; slots[i] = { ...slots[i], [key]: val }; return { ...f, slots }; });
  const openPicker = (e) => { try { e.currentTarget.showPicker?.(); } catch { /* noop */ } };

  const regenSlots = async () => {
    const svc = services.find((s) => s.id === form.service_id);
    try {
      const { data } = await api.post("/appointments/plan", {
        numero: parseInt(form.numero) || 1, data_inizio: form.data_inizio, data_fine: form.data_fine,
        frequenza_settimanale: form.frequenza_settimanale, durata_min: svc?.durata_min || 60,
        ora_preferita: form.ora_inizio || "09:00", collaborator_id: form.collaborator_id || null, stanza: form.stanza || "",
      });
      if (data.slots.length) setForm((f) => ({ ...f, slots: data.slots }));
      if (data.trovati < data.richiesti) toast.warning(data.messaggio);
      else toast.success(`${data.trovati} date proposte in base alle disponibilità`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveNewClient = async (e) => {
    e.preventDefault();
    if (!newClient.nome || !newClient.cognome) { toast.error("Nome e cognome obbligatori"); return; }
    setCreatingClient(true);
    try {
      const { data } = await api.post("/clients", { ...newClient });
      setClients((prev) => [...prev, data].sort((a, b) => (a.cognome || "").localeCompare(b.cognome || "")));
      setForm((f) => ({ ...f, client_id: data.id }));
      setNewClientOpen(false);
      setNewClient({ nome: "", cognome: "", telefono: "", email: "" });
      toast.success("Paziente creato e selezionato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreatingClient(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Seleziona un paziente"); return; }
    setSaving(true);
    try {
      const base = {
        client_id: form.client_id,
        collaborator_id: form.collaborator_id || null,
        service_id: form.service_id || null,
        stato: form.stato, stanza: form.stanza, note: form.note,
      };
      const single = editing || (parseInt(form.numero) || 1) === 1;
      if (editing) {
        const s = form.slots[0];
        await api.put(`/appointments/${editing.id}`, {
          ...base, inizio: isoFrom(s.data, s.ora_inizio), fine: isoFrom(s.data, s.ora_fine),
          importo: parseFloat(form.importo) || 0, lezioni_rimanenti: null,
          pagato: !!form.pagato, metodo_pagamento: form.metodo_pagamento || "",
        });
        toast.success("Appuntamento aggiornato");
      } else if (single) {
        const s = form.slots[0];
        await api.post("/appointments", {
          ...base, inizio: isoFrom(s.data, s.ora_inizio), fine: isoFrom(s.data, s.ora_fine),
          importo: parseFloat(form.importo) || 0,
        });
        toast.success("Appuntamento creato");
      } else {
        const slots = form.slots.map((s) => ({ inizio: isoFrom(s.data, s.ora_inizio), fine: isoFrom(s.data, s.ora_fine) }));
        const res = await api.post("/appointments/bulk", {
          ...base, importo: parseFloat(form.importo) || 0, dividi_importo: !!form.dividi_importo, slots,
        });
        toast.success(res.data.created > 1 ? `${res.data.created} appuntamenti pianificati` : "Appuntamento creato");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/appointments/${editing.id}`); toast.success("Appuntamento eliminato"); setOpen(false); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const apptsForDay = (day) => appts.filter((a) => isSameDay(parseISO(a.inizio), day)).sort((x, y) => x.inizio.localeCompare(y.inizio));

  const AppChip = ({ a }) => (
    <button onClick={(e) => { e.stopPropagation(); openEdit(a); }} data-testid={`appointment-${a.id}`}
            className="w-full text-left rounded-md px-2 py-1.5 text-xs transition-transform hover:scale-[1.01]"
            style={{ backgroundColor: `${a.collaborator_colore || "#17B8B0"}1a`, borderLeft: `3px solid ${a.collaborator_colore || "#17B8B0"}` }}>
      <div className="flex items-center gap-1 font-medium text-slate-800">
        <span className="font-mono-data">{format(parseISO(a.inizio), "HH:mm")}</span>
        <span className="truncate">{a.client_nome}</span>
      </div>
      {a.service_nome && <div className="truncate text-[11px] text-slate-500">{a.service_nome}</div>}
    </button>
  );

  const title = view === "month"
    ? format(cursor, "MMMM yyyy", { locale: it })
    : view === "week"
    ? `${format(range.start, "d MMM", { locale: it })} – ${format(range.end, "d MMM yyyy", { locale: it })}`
    : format(cursor, "EEEE d MMMM yyyy", { locale: it });

  const weekDays = view !== "month" ? eachDayOfInterval({ start: range.start, end: range.end }) : [];
  const monthDays = view === "month"
    ? eachDayOfInterval({ start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }) })
    : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Calendario</h1>
          <p className="text-sm text-muted-foreground mt-1 capitalize">{title}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={filterCol} onValueChange={setFilterCol}>
            <SelectTrigger className="w-[190px] bg-white" data-testid="filter-collaborator"><SelectValue placeholder="Collaboratore" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i collaboratori</SelectItem>
              {collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome} {c.cognome}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-lg border bg-white p-0.5">
            {[["day", "Giorno"], ["week", "Settimana"], ["month", "Mese"]].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`calendar-view-toggle-${v}`}
                      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${view === v ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div>
          <Button onClick={() => openNew()} data-testid="btn-nuovo-appuntamento" className="font-semibold">
            <Plus className="mr-1.5 h-4 w-4" /> Nuovo
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Oggi</Button>
        <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>

      <Card className="p-4 border-slate-200 overflow-x-auto">
        {view === "month" ? (
          <div className="min-w-[720px]">
            <div className="grid grid-cols-7 gap-px mb-1">
              {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => (
                <div key={d} className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-lg overflow-hidden">
              {monthDays.map((day) => {
                const dayAppts = apptsForDay(day);
                return (
                  <div key={day.toISOString()} onClick={() => openNew(day)}
                       className={`min-h-[104px] bg-white p-1.5 cursor-pointer transition-colors hover:bg-slate-50 ${!isSameMonth(day, cursor) ? "opacity-40" : ""}`}>
                    <div className={`text-xs font-medium mb-1 ${isSameDay(day, new Date()) ? "text-primary font-bold" : "text-slate-500"}`}>{format(day, "d")}</div>
                    <div className="space-y-1">
                      {dayAppts.slice(0, 3).map((a) => <AppChip key={a.id} a={a} />)}
                      {dayAppts.length > 3 && <div className="text-[10px] text-slate-400 pl-1">+{dayAppts.length - 3} altri</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className={`grid gap-3 ${view === "day" ? "grid-cols-1" : "grid-cols-1 md:grid-cols-7 min-w-[720px]"}`}>
            {weekDays.map((day) => {
              const dayAppts = apptsForDay(day);
              return (
                <div key={day.toISOString()} className="rounded-lg border border-slate-100 bg-slate-50/40">
                  <div className={`flex items-center justify-between px-3 py-2 border-b border-slate-100 ${isSameDay(day, new Date()) ? "bg-primary/5" : ""}`}>
                    <div>
                      <div className="text-xs uppercase text-slate-400">{format(day, "EEE", { locale: it })}</div>
                      <div className={`text-sm font-semibold ${isSameDay(day, new Date()) ? "text-primary" : "text-slate-700"}`}>{format(day, "d MMM", { locale: it })}</div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openNew(day)}><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="p-2 space-y-1.5 min-h-[80px]">
                    {dayAppts.length === 0 ? (
                      <p className="text-[11px] text-slate-300 text-center py-3">Nessun appuntamento</p>
                    ) : dayAppts.map((a) => <AppChip key={a.id} a={a} />)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Appuntamento" : "Nuovo Appuntamento"}</DialogTitle>
            <DialogDescription>Pianifica un appuntamento associando paziente, servizio e collaboratore.</DialogDescription>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Paziente *</Label>
                <Select value={form.client_id} onValueChange={(v) => { if (v === "__new__") { setNewClientOpen(true); } else if (v) { setForm((f) => ({ ...f, client_id: v })); } }}>
                  <SelectTrigger className="mt-1.5" data-testid="appt-client-select">
                    <SelectValue placeholder="Seleziona paziente">
                      {(() => { const c = clients.find((x) => x.id === form.client_id); return c ? `${c.cognome} ${c.nome}` : null; })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.cognome} {c.nome}</SelectItem>)}
                    <SelectItem value="__new__" className="text-primary font-medium" data-testid="appt-new-client-option">+ Crea nuovo paziente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Servizio</Label>
                  <Select value={form.service_id} onValueChange={onServiceChange}>
                    <SelectTrigger className="mt-1.5" data-testid="appt-service-select"><SelectValue placeholder="Servizio" /></SelectTrigger>
                    <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Collaboratore</Label>
                  <Select value={form.collaborator_id} onValueChange={(v) => setForm((f) => ({ ...f, collaborator_id: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="appt-collaborator-select"><SelectValue placeholder="Collaboratore" /></SelectTrigger>
                    <SelectContent>{collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome} {c.cognome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!editing && (
                  <div><Label>Numero appuntamenti *</Label><Input type="number" min="1" max="60" value={form.numero} onChange={(e) => setNumero(e.target.value)} onBlur={normalizeNumero} data-testid="appt-numero-input" className="mt-1.5" /></div>
                )}
                <div><Label>Stanza</Label><Input value={form.stanza} onChange={(e) => setForm((f) => ({ ...f, stanza: e.target.value }))} className="mt-1.5" /></div>
              </div>
              {(editing || (parseInt(form.numero) || 1) === 1) && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div><Label>Data *</Label><Input type="date" required value={form.slots[0].data} onChange={(e) => updateSlot(0, "data", e.target.value)} onClick={openPicker} data-testid="appt-data-input" className="mt-1.5" /></div>
                  <div><Label>Ora inizio *</Label><Input type="time" required value={form.slots[0].ora_inizio} onChange={(e) => updateSlot(0, "ora_inizio", e.target.value)} onClick={openPicker} data-testid="appt-ora-inizio-input" className="mt-1.5" /></div>
                  <div><Label>Ora fine *</Label><Input type="time" required value={form.slots[0].ora_fine} onChange={(e) => updateSlot(0, "ora_fine", e.target.value)} onClick={openPicker} data-testid="appt-ora-fine-input" className="mt-1.5" /></div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Stato</Label>
                  <Select value={form.stato} onValueChange={(v) => setForm((f) => ({ ...f, stato: v }))}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Importo (€)</Label><Input type="number" step="0.01" value={form.importo} onChange={(e) => setForm((f) => ({ ...f, importo: e.target.value }))} className="mt-1.5" /></div>
              </div>

              {editing && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 space-y-3" data-testid="payment-section">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-emerald-600" />
                      <span className="font-heading text-sm font-semibold text-slate-800">Pagamento</span>
                    </div>
                    <Switch checked={form.pagato} onCheckedChange={(v) => setForm((f) => ({ ...f, pagato: v }))} data-testid="appt-pagato-switch" />
                  </div>
                  {form.pagato && (
                    <div>
                      <Label className="text-sm">Metodo di pagamento</Label>
                      <Select value={form.metodo_pagamento} onValueChange={(v) => setForm((f) => ({ ...f, metodo_pagamento: v }))}>
                        <SelectTrigger className="mt-1.5 bg-white" data-testid="appt-metodo-select"><SelectValue placeholder="Seleziona metodo" /></SelectTrigger>
                        <SelectContent>{methods.map((m) => <SelectItem key={m.id} value={m.nome}>{m.nome}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <p className="text-[11px] text-emerald-700">
                    {form.pagato ? "Pagamento confermato: comparirà nei movimenti Incassi." : "Attiva per confermare l'incasso di questo appuntamento."}
                  </p>
                </div>
              )}

              {!editing && (parseInt(form.numero) || 1) > 1 && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-4" data-testid="multi-planning-section">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Repeat className="h-4 w-4 text-primary" />
                      <span className="font-heading text-sm font-semibold text-slate-800">Pianificazione sedute</span>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={regenSlots} data-testid="appt-generate-btn">Rigenera date</Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label>Inizio (data)</Label><Input type="date" value={form.data_inizio} onChange={(e) => setDataInizio(e.target.value)} onClick={openPicker} data-testid="appt-inizio-input" className="mt-1.5" /></div>
                    <div><Label>Fine (data)</Label><Input type="date" value={form.data_fine} min={form.data_inizio} onChange={(e) => setDataFine(e.target.value)} onClick={openPicker} data-testid="appt-fine-input" className="mt-1.5" /></div>
                  </div>
                  <div>
                    <Label>Frequenza settimanale</Label>
                    <Select value={String(form.frequenza_settimanale)} onValueChange={(v) => setFrequenza(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 bg-white" data-testid="appt-frequenza-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 volta a settimana</SelectItem>
                        <SelectItem value="2">2 volte a settimana</SelectItem>
                        <SelectItem value="3">3 volte a settimana</SelectItem>
                        <SelectItem value="4">4 volte a settimana</SelectItem>
                        <SelectItem value="5">5 volte a settimana</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Le date vengono proposte automaticamente in base agli slot disponibili nel calendario.
                  </p>
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {form.slots.map((s, i) => (
                      <div key={i} className="flex flex-wrap items-end gap-2 rounded-md border bg-white p-2" data-testid={`appt-slot-${i}`}>
                        <span className="w-5 pb-2 text-xs font-mono-data text-slate-400">{i + 1}</span>
                        <div className="min-w-[120px] flex-1"><Label className="text-[11px] text-slate-500">Data</Label><Input type="date" value={s.data} onChange={(e) => updateSlot(i, "data", e.target.value)} onClick={openPicker} data-testid={`slot-data-${i}`} className="mt-1 h-9" /></div>
                        <div className="w-[88px]"><Label className="text-[11px] text-slate-500">Inizio</Label><Input type="time" value={s.ora_inizio} onChange={(e) => updateSlot(i, "ora_inizio", e.target.value)} onClick={openPicker} data-testid={`slot-ora-inizio-${i}`} className="mt-1 h-9 px-1" /></div>
                        <div className="w-[88px]"><Label className="text-[11px] text-slate-500">Fine</Label><Input type="time" value={s.ora_fine} onChange={(e) => updateSlot(i, "ora_fine", e.target.value)} onClick={openPicker} data-testid={`slot-ora-fine-${i}`} className="mt-1 h-9 px-1" /></div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between rounded-md border bg-white px-3 py-2">
                    <div>
                      <Label className="text-sm">Dividi importo tra le sedute</Label>
                      <p className="text-[11px] text-muted-foreground">
                        {form.dividi_importo
                          ? `€ ${((parseFloat(form.importo) || 0) / (form.slots.length || 1)).toFixed(2)} per seduta`
                          : `€ ${parseFloat(form.importo) || 0} per ogni seduta`}
                      </p>
                    </div>
                    <Switch checked={form.dividi_importo} onCheckedChange={(v) => setForm((f) => ({ ...f, dividi_importo: v }))} data-testid="appt-dividi-switch" />
                  </div>
                </div>
              )}

              <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={2} className="mt-1.5" /></div>
              <DialogFooter className="gap-2 sm:justify-between">
                {editing ? (
                  <Button type="button" variant="outline" onClick={remove} data-testid="delete-appointment-button" className="text-destructive border-destructive/30 hover:bg-destructive/5">
                    <Trash2 className="mr-1.5 h-4 w-4" /> Elimina
                  </Button>
                ) : <span />}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                  <Button type="submit" disabled={saving} data-testid="save-appointment-button">{saving ? "Salvataggio..." : "Salva"}</Button>
                </div>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newClientOpen} onOpenChange={setNewClientOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md">
          <DialogHeader>
            <DialogTitle>Nuovo Paziente</DialogTitle>
            <DialogDescription>Verrà salvato nell'Anagrafica Clienti e selezionato nell'appuntamento.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveNewClient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nome *</Label><Input required value={newClient.nome} onChange={(e) => setNewClient((c) => ({ ...c, nome: e.target.value }))} data-testid="newclient-nome-input" className="mt-1.5" /></div>
              <div><Label>Cognome *</Label><Input required value={newClient.cognome} onChange={(e) => setNewClient((c) => ({ ...c, cognome: e.target.value }))} data-testid="newclient-cognome-input" className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Telefono</Label><Input value={newClient.telefono} onChange={(e) => setNewClient((c) => ({ ...c, telefono: e.target.value }))} data-testid="newclient-telefono-input" className="mt-1.5" /></div>
              <div><Label>Email</Label><Input type="email" value={newClient.email} onChange={(e) => setNewClient((c) => ({ ...c, email: e.target.value }))} data-testid="newclient-email-input" className="mt-1.5" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewClientOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={creatingClient} data-testid="save-newclient-button">{creatingClient ? "Creazione..." : "Crea e seleziona"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
